// Manufacturingdata.js - UI matched with Ordersproduct (No Scroller, No Field Override)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaIndustry, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaBarcode, FaMapMarkerAlt } from 'react-icons/fa';
import { RiDeleteBinFill } from 'react-icons/ri';
import Swal from 'sweetalert2';
import { IoMdArrowDropdown } from "react-icons/io";
import './Manufacturingdata.css';

// Last loaded records of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const MD_CACHE_PREFIX = 'manufacturing_cache_';
const MD_CITIES_PREFIX = 'manufacturing_cities_';
const MD_SHOPS_KEY = 'manufacturing_shops_cache';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const Manufacturingdata = () => {
  const navigate = useNavigate();
  const { shopId } = useParams();

  // The shop is known from the very first render (from the URL or the saved selection),
  // so the list never shows the records of ALL shops for a moment, and the saved list
  // of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const storedId = parseInt(localStorage.getItem('selectedShopId'));
    const storedShopId = Number.isNaN(storedId) ? null : storedId;
    const startShop = (shopId && shopId !== 'all') ? String(shopId) : (storedShopId ? String(storedShopId) : null);
    const cachedData = readJson(`${MD_CACHE_PREFIX}${startShop || 'all'}`);
    const cachedCities = startShop ? readJson(`${MD_CITIES_PREFIX}${startShop}`) : null;
    const cachedShops = readJson(MD_SHOPS_KEY);
    initialRef.current = {
      storedShopId,
      startShop,
      data: Array.isArray(cachedData) ? cachedData : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null,
      shops: Array.isArray(cachedShops) ? cachedShops : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // NOTE: holds ALL records of the shop (not filtered by city) so city counts stay correct
  const [manufacturingData, setManufacturingData] = useState(initialRef.current.data || []);
  // True only while there is nothing to show yet (so "No manufacturing records found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.data);
  // loading = a delete is running (disables the buttons)
  const [loading, setLoading] = useState(false);
  const [shopName, setShopName] = useState('');
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [selectedCity, setSelectedCity] = useState('all');
  const [shops, setShops] = useState(initialRef.current.shops || []);

  // Get selected shop from localStorage (set by Navbar)
  const [selectedShopId, setSelectedShopId] = useState(initialRef.current.storedShopId);
  const [selectedShopName, setSelectedShopName] = useState(localStorage.getItem('selectedShopName') || '');

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

  // the shop whose records are shown (URL shop first, then the saved selection)
  const effectiveShopId = (shopId && shopId !== 'all')
    ? String(shopId)
    : (selectedShopId ? String(selectedShopId) : null);

  const dataRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const prevShopRef = useRef(initialRef.current.startShop);
  const citiesFromServerRef = useRef(!!initialRef.current.cities);

  // SweetAlert functions
  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#004aad'
    });
  };

  const showErrorAlert = (message, details = '') => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      html: `${message}<br/><small style="color: #666;">${details}</small>`,
      confirmButtonColor: '#d33'
    });
  };

  const showDeleteConfirmation = (count, isMultiple = false) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} item(s)?`
      : 'Are you sure you want to delete this item?';

    return Swal.fire({
      title: 'Are you sure?',
      text: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  // Get selected shop from localStorage
  const getSelectedShopFromStorage = () => {
    const storedShopId = localStorage.getItem('selectedShopId');
    const storedShopName = localStorage.getItem('selectedShopName');
    return { shopId: storedShopId, shopName: storedShopName };
  };

  // Fetch shops (once)
  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/shops/`);
      if (response.ok) {
        const data = await response.json();
        const shopsList = Array.isArray(data) ? data : [];
        setShops(shopsList);
        writeJson(MD_SHOPS_KEY, shopsList);

        const stored = getSelectedShopFromStorage();
        if (stored.shopId) {
          setSelectedShopId(parseInt(stored.shopId));
          setSelectedShopName(stored.shopName || '');
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  // Fetch cities for selected shop
  const fetchCities = async (shopIdParam) => {
    if (!shopIdParam) return;
    const requestId = ++citiesRequestRef.current;

    try {
      const response = await fetch(`${API_BASE_URL}/inventory/shop-cities/?shop_id=${shopIdParam}`);
      if (requestId !== citiesRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        if (requestId !== citiesRequestRef.current) return;
        citiesFromServerRef.current = true;
        if (data.cities && data.cities.length > 0) {
          setCities(data.cities);
          writeJson(`${MD_CITIES_PREFIX}${shopIdParam}`, data.cities);
        } else {
          setCities([]);
        }
      }
    } catch (error) {
      if (requestId !== citiesRequestRef.current) return;
      console.error('Error fetching cities:', error);
    }
  };

  // Fetch manufacturing data - filtered by shop only (city filter is applied on frontend
  // so that every city can show its own count). Runs in the background: only the latest
  // request is allowed to update the screen.
  const fetchManufacturingData = async (shopIdParam) => {
    const requestId = ++dataRequestRef.current;
    const cacheKey = `${MD_CACHE_PREFIX}${shopIdParam || 'all'}`;

    try {
      let url = `${API_BASE_URL}/inventory/api/manufacturing/`;
      const params = new URLSearchParams();

      if (shopIdParam) {
        params.append('shop_id', shopIdParam);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url);
      if (requestId !== dataRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (requestId !== dataRequestRef.current) return;
        const list = Array.isArray(data) ? data : [];
        setManufacturingData(list);
        writeJson(cacheKey, list);
      } else {
        console.error('❌ Failed to fetch manufacturing data');
      }
    } catch (error) {
      if (requestId !== dataRequestRef.current) return;
      console.error('❌ Error fetching manufacturing data:', error);
      showErrorAlert('Failed to fetch manufacturing data', error.message);
    } finally {
      if (requestId === dataRequestRef.current) setIsLoading(false);
    }
  };

  // Shops list is loaded once
  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Records and cities load together instead of one after another.
  // (city change does not refetch - it filters locally)
  useEffect(() => {
    if (prevShopRef.current !== effectiveShopId) {
      prevShopRef.current = effectiveShopId;
      const cachedData = readJson(`${MD_CACHE_PREFIX}${effectiveShopId || 'all'}`);
      const cachedCities = effectiveShopId ? readJson(`${MD_CITIES_PREFIX}${effectiveShopId}`) : null;
      if (Array.isArray(cachedData)) {
        setManufacturingData(cachedData);
        setIsLoading(false);
      } else {
        setManufacturingData([]);
        setIsLoading(true);
      }
      citiesFromServerRef.current = Array.isArray(cachedCities);
      setCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedCity('all');
      setSelectedItems([]);
    }

    fetchManufacturingData(effectiveShopId);
    if (effectiveShopId) fetchCities(effectiveShopId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveShopId]);

  // Shop name (and cities, only if the server has not given them yet) from the shops list
  useEffect(() => {
    if (!effectiveShopId || shops.length === 0) return;
    const currentShop = shops.find(shop => shop.shop_id.toString() === effectiveShopId);
    if (currentShop) {
      setShopName(currentShop.shop_name);
      if (!citiesFromServerRef.current && currentShop.cities && currentShop.cities.length > 0) {
        setCities(currentShop.cities);
      }
    }
  }, [shops, effectiveShopId]);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedShopId' || e.key === 'selectedShopName') {
        const storedShopId = localStorage.getItem('selectedShopId');
        const storedShopName = localStorage.getItem('selectedShopName');

        if (storedShopId) {
          // the data of the new shop is loaded by the effect above
          setSelectedShopId(parseInt(storedShopId));
          setSelectedShopName(storedShopName || '');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedItems([]);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Deleted records disappear right away (and from the saved copy too)
  const removeItemsLocally = (ids) => {
    setManufacturingData(prev => {
      const next = prev.filter(item => !ids.includes(item.id));
      writeJson(`${MD_CACHE_PREFIX}${effectiveShopId || 'all'}`, next);
      return next;
    });
  };

  // ✅ Count of records per city (always from full shop data)
  const cityCounts = manufacturingData.reduce((acc, item) => {
    if (item.city) acc[item.city] = (acc[item.city] || 0) + 1;
    return acc;
  }, {});

  const totalCount = manufacturingData.length;
  const selectedCityCount = selectedCity === 'all' ? totalCount : (cityCounts[selectedCity] || 0);
  const selectedCityLabel = selectedCity === 'all' ? 'All Cities' : selectedCity;

  const filteredData = manufacturingData.filter(item => {
    const cityMatch = selectedCity === 'all' || item.city === selectedCity;
    const searchMatch =
      item.part_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.lot_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.part_reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return cityMatch && searchMatch;
  });

  const handleSelectAll = () => {
    if (selectedItems.length === filteredData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredData.map(item => item.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      showErrorAlert('Please select items to delete');
      return;
    }

    const result = await showDeleteConfirmation(selectedItems.length, true);

    if (result.isConfirmed) {
      setLoading(true);
      try {
        let deletedCount = 0;
        const deletedIds = [];
        for (const itemId of selectedItems) {
          const response = await fetch(`${API_BASE_URL}/inventory/api/manufacturing/${itemId}/delete/`, {
            method: 'DELETE',
          });

          if (response.ok || response.status === 204) {
            deletedCount++;
            deletedIds.push(itemId);
          }
        }

        removeItemsLocally(deletedIds);
        showSuccessAlert(`${deletedCount} item(s) deleted successfully!`);
        setSelectedItems([]);
        setShowActionDropdown(false);
        fetchManufacturingData(effectiveShopId);
      } catch (error) {
        console.error('Error deleting items:', error);
        showErrorAlert('Failed to delete some items. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClearAll = () => {
    setSelectedItems([]);
    setShowActionDropdown(false);
  };

  const handleAddNew = () => {
    let shopIdToUse = null;

    if (shopId && shopId !== 'all') {
      shopIdToUse = shopId;
    } else if (selectedShopId) {
      shopIdToUse = selectedShopId;
    }

    if (shopIdToUse) {
      navigate(`/shop/${shopIdToUse}/AddManufacturingdata`);
    } else {
      navigate('/AddManufacturingdata');
    }
  };

  const handleEdit = (item) => {
    let shopIdToUse = null;

    if (shopId && shopId !== 'all') {
      shopIdToUse = shopId;
    } else if (selectedShopId) {
      shopIdToUse = selectedShopId;
    }

    if (shopIdToUse) {
      navigate(`/shop/${shopIdToUse}/AddManufacturingdata`, { state: { editData: item } });
    } else {
      navigate('/AddManufacturingdata', { state: { editData: item } });
    }
  };

  const handleDelete = async (itemId) => {
    const result = await showDeleteConfirmation(1);

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/inventory/api/manufacturing/${itemId}/delete/`, {
          method: 'DELETE',
        });

        if (response.ok || response.status === 204) {
          removeItemsLocally([itemId]);
          showSuccessAlert('Item deleted successfully!');
          setSelectedItems(selectedItems.filter(id => id !== itemId));
          fetchManufacturingData(effectiveShopId);
        } else {
          showErrorAlert('Failed to delete item');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        showErrorAlert('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getDisplayShopId = () => {
    if (shopId && shopId !== 'all') return shopId;
    if (selectedShopId) return selectedShopId;
    return null;
  };

  const getDisplayShopName = () => {
    if (shopId && shopId !== 'all') return shopName || `Shop ID: ${shopId}`;
    if (selectedShopName) return selectedShopName;
    return 'All Shops';
  };

  const displayShopId = getDisplayShopId();
  const displayShopName = getDisplayShopName();

  return (
    <div className="md-orders-container">
      {/* ✅ HEADER - EXACTLY like Ordersproduct */}
      <div className="md-header-section">
        <div className="md-header-left">
          <h2 className="md-header-title">
            <FaBarcode className="md-title-icon" />
            <span>Manufacturing Data</span>
          </h2>

          {/* ✅ City count badge */}
          <span className="md-city-badge">
            <FaMapMarkerAlt className="md-city-badge-icon" />
            {selectedCityLabel} ({selectedCityCount})
          </span>
        </div>

        <div className="md-header-right">
          {/* ✅ City Dropdown with Custom Arrow + counts */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedCity}
              onChange={handleCityChange}
              disabled={cities.length === 0}
            >
              <option value="all">
                {cities.length === 0 ? 'No Cities' : `All Cities (${totalCount})`}
              </option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-arrow" />
          </div>

          {/* ✅ Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Part Name, Lot Number, or Reference Number..."
              className="md-search-bar"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          {/* ✅ Select All */}
          <button className="md-select-btn" onClick={handleSelectAll} disabled={loading}>
            <FaCheckDouble />
            <span>{selectedItems.length === filteredData.length && filteredData.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>

          {/* ✅ Add Manufacturing Data */}
          <button className="md-add-btn" onClick={handleAddNew} disabled={loading}>
            <FaPlus />
            <span>ADD MANUFACTURING DATA</span>
          </button>
        </div>
      </div>

      <div className="md-content">
        {/* Selected Summary */}
        {selectedItems.length > 0 && (
          <div className="md-selection-bar">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedItems.length} item(s) selected
              </span>
            </div>
            <div className="action-dropdown-container">
              <button
                className="md-action-btn"
                onClick={() => setShowActionDropdown(!showActionDropdown)}
                disabled={loading}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="md-action-dropdown">
                  <button
                    className="md-delete-option"
                    onClick={handleDeleteSelected}
                    disabled={loading}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedItems.length})</span>
                  </button>
                </div>
              )}
              <button className="md-clear-btn" onClick={handleClearAll} disabled={loading}>
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* ✅ Table - Blue border, NO SCROLLER, fields auto-fit */}
        <div className="md-table-container">
          <table className="md-table">
            <thead>
              <tr>
                <th className="md-col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredData.length && filteredData.length > 0}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                </th>
                <th>Manufacturing Date</th>
                <th>Lot Number</th>
                <th>Part Name</th>
                <th>Part Type</th>
                <th>Part Reference Number</th>
                <th>Machine Operator</th>
                <th>Machine Inspection</th>
                <th>Part Per Weight (kg)</th>
                <th>Total Part Weight (kg)</th>
                <th>Quantity</th>
                <th>City</th>
                <th>Shop</th>
                <th className="md-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No manufacturing records found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="14" style={{ height: '200px' }}></td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item.id} className={selectedItems.includes(item.id) ? 'selected' : ''}>
                    <td className="md-col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        disabled={loading}
                      />
                    </td>
                    <td>{formatDate(item.manufacturing_date)}</td>
                    <td><strong>{item.lot_number}</strong></td>
                    <td className="part-name-cell">{item.part_name}</td>
                    <td>{item.part_type || '-'}</td>
                    <td>{item.part_reference_number || '-'}</td>
                    <td>{item.machine_operator || '-'}</td>
                    <td>
                      <span className={`inspection-badge ${item.machine_inspection?.toLowerCase()}`}>
                        {item.machine_inspection || '-'}
                      </span>
                    </td>
                    <td>{item.part_per_weight || '-'}</td>
                    <td>{item.total_part_weight || '-'}</td>
                    <td>{item.quantity || '-'}</td>
                    <td>{item.city || '-'}</td>
                    <td>{item.shop?.shop_name || 'N/A'}</td>
                    <td className="md-actions-cell">
                      <div className="md-actions-cell-inner">
                        <button
                          className="md-edit-icon-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit Item"
                          disabled={loading}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="md-delete-icon-btn"
                          onClick={() => handleDelete(item.id)}
                          title="Delete Item"
                          disabled={loading}
                        >
                          <RiDeleteBinFill />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="14" className="no-data-cell">
                    <div className="no-data">
                      <FaIndustry className="no-data-icon" />
                      <p>
                        {displayShopId
                          ? `No manufacturing records found for ${displayShopName}.`
                          : 'No manufacturing records found.'}
                      </p>
                      <button
                        className="add-first-btn"
                        onClick={handleAddNew}
                        disabled={loading}
                      >
                        <FaPlus /> Add Your First Record
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Manufacturingdata;